import { ImageAspectRatio } from "common/components/image-types";
import type { TestIframeEmbeddableBody } from "common/firebase-function-types";
import { assert, dontAwait, liftURLAssumingHTTP } from "common/Support";
import * as React from "react";
import * as routes from "routes";
import { callCloudFunctionWeb } from "sharedUI/lib/function-utils";
import { WebviewWrapper } from "./webview-style";

type Props = Readonly<{
    link: string;
    aspectRatio: ImageAspectRatio;
    title: string | undefined;
    allowScrolling: boolean;
    explicitHeight: number | undefined;
}>;

interface State {
    // starts out as `true` if we don't have the data yet
    readonly isEmbeddable: boolean;
}

const isEmbeddableCache = new Map<string, boolean>();

function parseURLAssumingHTTP(url: string): URL | undefined {
    return liftURLAssumingHTTP(url, u => new URL(u));
}

function getSchemeFromPossibleURL(possibleURL: string): "data" | "http" | "https" | undefined {
    const maybeURL = parseURLAssumingHTTP(possibleURL);
    if (maybeURL === undefined) return undefined;
    const { protocol } = maybeURL;
    switch (protocol) {
        case "data:":
            return "data";
        case "http:":
            return "http";
        case "https:":
            return "https";
        default:
            return undefined;
    }
}

const noSign = (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M27.3134 4.712C24.3111 1.71152 20.2399 0.0265672 15.9953 0.0278175C11.7507 0.0290677 7.68052 1.71642 4.68003 4.71867C1.67955 7.72092 -0.00539991 11.7921 -0.0041497 16.0367C-0.00289948 20.2813 1.68445 24.3515 4.6867 27.352C7.64423 30.2463 11.5983 31.8976 15.7358 31.9663C19.8734 32.035 23.8801 30.5159 26.932 27.7213C28.2695 26.4704 29.3837 25.0004 30.2267 23.3747C31.0323 21.8105 31.573 20.1237 31.8267 18.3827C31.9411 17.6043 31.999 16.8187 32 16.032C32.0058 13.929 31.5945 11.8458 30.7901 9.90276C29.9856 7.95973 28.804 6.19546 27.3134 4.712ZM7.05603 7.056C9.19029 4.96597 11.9869 3.68611 14.9637 3.43711C17.9405 3.18812 20.9109 3.98558 23.3627 5.692C23.4038 5.72196 23.4378 5.76046 23.4626 5.80485C23.4874 5.84924 23.5022 5.89847 23.5062 5.94915C23.5101 5.99983 23.503 6.05076 23.4854 6.09843C23.4677 6.14611 23.44 6.1894 23.404 6.22533L6.2227 23.4147C6.18666 23.4504 6.14334 23.478 6.09569 23.4955C6.04804 23.513 5.99718 23.52 5.94657 23.5161C5.89596 23.5122 5.84679 23.4974 5.80241 23.4727C5.75802 23.4481 5.71947 23.4142 5.68937 23.3733C3.94908 20.9318 3.13133 17.952 3.38158 14.9641C3.63183 11.9763 4.93385 9.17408 7.05603 7.056ZM24.9987 25.008C22.8784 27.1231 20.0784 28.4201 17.0939 28.6697C14.1095 28.9194 11.1329 28.1055 8.6907 26.372C8.64955 26.3421 8.61535 26.3037 8.59046 26.2594C8.56558 26.215 8.55059 26.1658 8.54653 26.1152C8.54248 26.0645 8.54945 26.0135 8.56697 25.9658C8.5845 25.9181 8.61215 25.8747 8.64803 25.8387L25.8307 8.648C25.8667 8.61225 25.9101 8.58469 25.9577 8.56719C26.0054 8.54969 26.0562 8.54266 26.1068 8.54658C26.1574 8.5505 26.2066 8.56529 26.251 8.58992C26.2954 8.61455 26.3339 8.64846 26.364 8.68933C28.105 11.1307 28.9234 14.1106 28.6737 17.0987C28.4239 20.0869 27.1222 22.8895 25 25.008H24.9987Z"
            fill="#999999"
        />
    </svg>
);

export class Webview extends React.PureComponent<Props, State> {
    state = { isEmbeddable: true };

    webviewRef = React.createRef<HTMLDivElement>();

    private checkEmbeddable() {
        const { link } = this.props;
        const isEmbeddable = routes.isPlayer() ? true : isEmbeddableCache.get(link);
        this.setState({ isEmbeddable: isEmbeddable !== false });

        if (isEmbeddable === undefined) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            dontAwait(this.fetchIsEmbeddable());
        }
    }

    private async fetchIsEmbeddable(): Promise<void> {
        const { link } = this.props;

        const possibleScheme = getSchemeFromPossibleURL(link);
        if (possibleScheme === undefined) {
            isEmbeddableCache.set(link, false);
            return;
        } else if (possibleScheme === "data") {
            isEmbeddableCache.set(link, true);
            return;
        }

        const body: TestIframeEmbeddableBody = { url: link, referer: window.location.href };
        const response = await callCloudFunctionWeb("testIframeEmbeddable", body);

        if (response === undefined || !response.ok) return;
        const { isEmbeddable } = await response.json();
        assert(typeof isEmbeddable === "boolean");
        isEmbeddableCache.set(link, isEmbeddable);
        this.setState({ isEmbeddable });
    }

    private forceEmbeddable = () => {
        this.setState({ isEmbeddable: true });
        return undefined;
    };

    public componentDidMount() {
        this.checkEmbeddable();
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.link === this.props.link) return;

        this.checkEmbeddable();
    }

    public render(): React.ReactNode {
        const { link, aspectRatio, title, allowScrolling, explicitHeight } = this.props;
        const { isEmbeddable } = this.state;
        let classOverlaySuffix = explicitHeight === undefined ? "fill" : "explicit";
        switch (aspectRatio) {
            case ImageAspectRatio.ThreeByOne:
                classOverlaySuffix = "three-by-one";
                break;
            case ImageAspectRatio.ThreeByTwo:
                classOverlaySuffix = "three-by-two";
                break;
            case ImageAspectRatio.Square:
                classOverlaySuffix = "square";
                break;
            case ImageAspectRatio.ThreeByFour:
                classOverlaySuffix = "three-by-four";
                break;
            case ImageAspectRatio.Expand:
                classOverlaySuffix = "expand";
                break;
        }
        let content: React.ReactNode;
        const liftedHost = parseURLAssumingHTTP(link);
        if (isEmbeddable && liftedHost !== undefined) {
            const sandboxed = ["forms", "popups", "scripts"];
            const allowed = ["camera", "microphone", "geolocation", "payment"];
            if (liftedHost.origin !== window.location.origin) {
                // Embeds tend to break if their same-origin assumptions are incorrect.
                // However, if we allow same-origin assumptions to hold on _our_ origin,
                // that's a security hole. So we only allow this if the origins are
                // different.
                sandboxed.push("same-origin");
            }
            const sandbox = sandboxed.map(f => `allow-${f}`).join(" ");
            content = (
                <iframe
                    src={liftedHost.href}
                    title={title}
                    referrerPolicy="no-referrer-when-downgrade"
                    sandbox={sandbox}
                    allow={allowed.join(";")}
                    scrolling={allowScrolling ? "yes" : "no"}
                />
            );
        } else {
            const targetHost = liftedHost?.hostname;
            const isBadLink = getSchemeFromPossibleURL(link) === undefined || targetHost === undefined;
            const message = isBadLink
                ? `${link} cannot be opened`
                : `${targetHost} may not allow itself to be embedded`;
            content = (
                <div className="embedding-not-possible">
                    <div className="no-container">{noSign}</div>
                    <div>{message}</div>
                    {isBadLink ? null : <button onClick={this.forceEmbeddable}>Try again</button>}
                </div>
            );
        }
        return (
            <WebviewWrapper
                data-test="app-web-view"
                className={`inline-link-${classOverlaySuffix}`}
                explicitHeight={explicitHeight}
                ref={this.webviewRef}>
                {content}
            </WebviewWrapper>
        );
    }
}
